/**
 * DoorEditor - Shared component for editing a single door's properties.
 *
 * Used by DoorListEditor, SingleRowUnitDoorEditor, and DoubleRowUnitDoorEditor.
 * Includes auto-center option that keeps the door centered on the wall when width changes.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Trash2 } from 'lucide-react'
import type { DoorSpec, DoorType } from '@/lib/templates/types'

export interface DoorEditorProps {
  door: DoorSpec
  index: number
  wallLength: number
  maxHeight: number
  onUpdate: (updates: Partial<DoorSpec>) => void
  onRemove: () => void
}

export function DoorEditor({
  door,
  index,
  wallLength,
  maxHeight,
  onUpdate,
  onRemove,
}: DoorEditorProps) {
  const [autoCenter, setAutoCenter] = useState(true)

  const handleWidthChange = (newWidth: number) => {
    if (autoCenter) {
      onUpdate({
        width_ft: newWidth,
        offset_ft: Math.max(0, (wallLength - newWidth) / 2),
      })
    } else {
      onUpdate({ width_ft: newWidth })
    }
  }

  const handleAutoCenterToggle = (checked: boolean) => {
    setAutoCenter(checked)
    if (checked) {
      onUpdate({ offset_ft: Math.max(0, (wallLength - door.width_ft) / 2) })
    }
  }

  return (
    <div className="border rounded-lg p-3 bg-muted/30 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          Door {index + 1}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-destructive hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id={`autocenter-${index}`}
          checked={autoCenter}
          onCheckedChange={(checked) => handleAutoCenterToggle(checked === true)}
        />
        <Label htmlFor={`autocenter-${index}`} className="text-xs cursor-pointer">
          Auto-center on wall
        </Label>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Offset (ft)</Label>
          <Input
            type="number"
            min={0}
            max={wallLength}
            step="any"
            value={door.offset_ft}
            onChange={(e) => onUpdate({ offset_ft: parseFloat(e.target.value) || 0 })}
            disabled={autoCenter}
            className="h-8 text-sm"
          />
          <p className="text-xs text-muted-foreground">
            {autoCenter ? 'Centered' : 'Edge position'}
          </p>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Width (ft)</Label>
          <Input
            type="number"
            min={10}
            max={200}
            step="any"
            value={door.width_ft}
            onChange={(e) => handleWidthChange(parseFloat(e.target.value) || 10)}
            className="h-8 text-sm"
          />
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Height (ft)</Label>
          <Input
            type="number"
            min={8}
            max={maxHeight}
            step="any"
            value={door.height_ft}
            onChange={(e) => onUpdate({ height_ft: parseFloat(e.target.value) || 8 })}
            className="h-8 text-sm"
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Type</Label>
        <Select
          value={door.type}
          onValueChange={(value) => onUpdate({ type: value as DoorType })}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bifold">Bi-fold</SelectItem>
            <SelectItem value="sliding">Sliding</SelectItem>
            <SelectItem value="hydraulic">Hydraulic</SelectItem>
            <SelectItem value="rolling">Rolling</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
